import React, { ChangeEvent, ComponentProps, useEffect, useMemo, useReducer, useState } from 'react';

import { Stack, TextField, Typography } from '@mui/material';

import { formatDateISO, newDate } from '~/services/TimeService';

import DateTimeWidget from '../../Elements/DateTimeWidget';

// subset of Reservation.Discount
// typing is a bit wack here
interface ScheduledDiscount {
  id?: string;
  rate_plan_id: string;
  discount_type: string;
  discount_percent: number | string;
  luxplus_discount_percent: number | null | string;
  min_los: number | null | string;
  max_los: number | null | string;
  min_days_to_arrival: number | null | string;
  max_days_to_arrival: number | null | string;
  discount_name: string | null;
  is_displayed: boolean;
  source: 'internal';
  paid_by: 'vendor';
  schedules: Array<{
    activePeriod: {
      from: string;
      to: string;
    };
    travelPeriod: {
      from: string;
      to: string;
    };
    type: 'sell';
    id?: string;
    parent?: string;
  }>;
}

interface ScheduledDiscountProps {
  value: ScheduledDiscount;
  onChange: (value: ScheduledDiscount) => void;
  sx?: ComponentProps<typeof Stack>['sx'];
}

type Action =
  | {
      type: 'setActivePeriodFrom' | 'setActivePeriodTo' | 'setTravelPeriodFrom' | 'setTravelPeriodTo';
      date: string;
    }
  | {
      type: 'setDiscountName';
      value: string | null;
    }
  | {
      type:
        | 'setDiscountPercentage'
        | 'setLuxDiscountPercentage'
        | 'setMinLos'
        | 'setMaxLos'
        | 'setMinDaysToArrival'
        | 'setMaxDaysToArrival';
      value: number | null;
    };

type State = ScheduledDiscount;

function reducer(oldState: State, action: Action) {
  switch (action.type) {
    case 'setDiscountName':
      return {
        ...oldState,
        discount_name: action.value,
      };
    case 'setDiscountPercentage':
      return {
        ...oldState,
        discount_percent: action.value,
      };
    case 'setLuxDiscountPercentage':
      return {
        ...oldState,
        luxplus_discount_percent: action.value,
      };
    case 'setMinLos':
      return {
        ...oldState,
        min_los: action.value,
      };
    case 'setMaxLos':
      return {
        ...oldState,
        max_los: action.value,
      };
    case 'setMinDaysToArrival':
      return {
        ...oldState,
        min_days_to_arrival: action.value,
      };
    case 'setMaxDaysToArrival':
      return {
        ...oldState,
        max_days_to_arrival: action.value,
      };
    case 'setActivePeriodFrom':
      return {
        ...oldState,
        schedules: [
          {
            ...oldState.schedules[0],
            activePeriod: { ...oldState.schedules[0].activePeriod, from: action.date },
          },
        ],
      };

    case 'setActivePeriodTo':
      return {
        ...oldState,
        schedules: [
          {
            ...oldState.schedules[0],
            activePeriod: { ...oldState.schedules[0].activePeriod, to: action.date },
          },
        ],
      };

    case 'setTravelPeriodFrom':
      return {
        ...oldState,
        schedules: [
          {
            ...oldState.schedules[0],
            travelPeriod: { ...oldState.schedules[0].travelPeriod, from: action.date },
          },
        ],
      };

    case 'setTravelPeriodTo':
      return {
        ...oldState,
        schedules: [
          {
            ...oldState.schedules[0],
            travelPeriod: { ...oldState.schedules[0].travelPeriod, to: action.date },
          },
        ],
      };
  }
}

function ScheduledDiscount(props: ScheduledDiscountProps) {
  const { value } = props;
  const formattedValue = useMemo(() => {
    return {
      ...value,
      discount_percent: value.discount_percent === '' ? 0 : value.discount_percent,
      luxplus_discount_percent: value.luxplus_discount_percent === '' ? null : value.luxplus_discount_percent,
      min_los: value.min_los === '' ? null : value.min_los,
      max_los: value.max_los === '' ? null : value.max_los,
      min_days_to_arrival: value.min_days_to_arrival === '' ? null : value.min_days_to_arrival,
      max_days_to_arrival: value.max_days_to_arrival === '' ? null : value.max_days_to_arrival,
    };
  }, [value]);

  const [state, dispatch] = useReducer(reducer, formattedValue);
  const [discountPercentageError, setDiscountPercentageError] = useState<boolean>(false);
  const [luxDiscountPercentageError, setLuxDiscountPercentageError] = useState<boolean>(false);
  const activeDayjs = useMemo(
    () => ({
      from: newDate(state.schedules[0].activePeriod.from),
      to: newDate(state.schedules[0].activePeriod.to),
    }),
    [state.schedules],
  );
  const travelDayjs = useMemo(
    () => ({
      from: newDate(state.schedules[0].travelPeriod.from),
      to: newDate(state.schedules[0].travelPeriod.to),
    }),
    [state.schedules],
  );

  useEffect(() => {
    props.onChange(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const handleDiscountChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    type: 'discount' | 'lux',
  ) => {
    const value = event.target.value;

    if (value === '') {
      dispatch({ type: type === 'discount' ? 'setDiscountPercentage' : 'setLuxDiscountPercentage', value: null });
      return;
    }

    if (!isNaN(Number(value))) {
      dispatch({
        type: type === 'discount' ? 'setDiscountPercentage' : 'setLuxDiscountPercentage',
        value: Number(value),
      });

      if (type === 'discount') {
        const isValidDiscountPercentage = Number(value) >= 0 && Number(value) <= 100;
        setDiscountPercentageError(!isValidDiscountPercentage);
      }

      if (type === 'lux') {
        const isValidLuxDiscountPercentage = Number(value) >= 0 && Number(value) <= 100;
        setLuxDiscountPercentageError(!isValidLuxDiscountPercentage);
      }
    }
  };

  const handleMinLosChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    if (value === '') {
      dispatch({ type: 'setMinLos', value: null });
    } else if (!isNaN(Number(value))) {
      dispatch({ type: 'setMinLos', value: Number(value) });
    }
  };

  const handleMaxLosChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    if (value === '') {
      dispatch({ type: 'setMaxLos', value: null });
    } else if (!isNaN(Number(value))) {
      dispatch({ type: 'setMaxLos', value: Number(value) });
    }
  };

  const handleDaysToArrivalChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    type: 'min' | 'max',
  ) => {
    const value = event.target.value;
    if (value === '') {
      dispatch({ type: type === 'min' ? 'setMinDaysToArrival' : 'setMaxDaysToArrival', value: null });
    } else if (!isNaN(Number(value))) {
      dispatch({ type: type === 'min' ? 'setMinDaysToArrival' : 'setMaxDaysToArrival', value: Number(value) });
    }
  };

  return (
    <Stack direction="column" sx={props.sx}>
      <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="flex-start">
        <Stack spacing={2}>
          <Typography>Discount Details</Typography>
          <TextField
            label="Discount Name"
            value={state.discount_name || ''}
            onChange={(event) => dispatch({ type: 'setDiscountName', value: event.target.value })}
          />
          <TextField
            label="Discount Percentage"
            value={state.discount_percent}
            onChange={(event) => handleDiscountChange(event, 'discount')}
            error={discountPercentageError}
            helperText={discountPercentageError ? 'Value must be between 0 and 100' : ' '}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography>Lux Plus</Typography>
          <TextField
            label="Lux Plus Discount %"
            value={state.luxplus_discount_percent}
            onChange={(event) => handleDiscountChange(event, 'lux')}
            error={luxDiscountPercentageError}
            helperText={luxDiscountPercentageError ? 'Value must be between 0 and 100' : ' '}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography>Length of Stay options</Typography>
          <TextField label="Min LOS" value={state.min_los || ''} onChange={handleMinLosChange} />
          <TextField label="Max LOS" value={state.max_los || ''} onChange={handleMaxLosChange} />
        </Stack>

        <Stack spacing={2}>
          <Typography>Days to Arrival options</Typography>
          <TextField
            label="Min Days to Arrival"
            value={state.min_days_to_arrival || ''}
            onChange={(event) => handleDaysToArrivalChange(event, 'min')}
          />
          <TextField
            label="Max Days to Arrival"
            value={state.max_days_to_arrival || ''}
            onChange={(event) => handleDaysToArrivalChange(event, 'max')}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography>Active Period</Typography>
          <DateTimeWidget
            label="From"
            format="DD-MM-YYYY"
            showDateOnly
            value={state.schedules[0].activePeriod.from}
            maxDate={activeDayjs.to}
            onChange={(date) => dispatch({ type: 'setActivePeriodFrom', date: formatDateISO(date) })}
          />
          <DateTimeWidget
            label="To"
            format="DD-MM-YYYY"
            showDateOnly
            value={state.schedules[0].activePeriod.to}
            minDate={activeDayjs.from}
            onChange={(date) => dispatch({ type: 'setActivePeriodTo', date: formatDateISO(date) })}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography>Travel Period</Typography>
          <DateTimeWidget
            label="From"
            format="DD-MM-YYYY"
            showDateOnly
            value={state.schedules[0].travelPeriod.from}
            maxDate={travelDayjs.to.subtract(1, 'day')}
            onChange={(date) => dispatch({ type: 'setTravelPeriodFrom', date: formatDateISO(date) })}
          />
          <DateTimeWidget
            label="To"
            format="DD-MM-YYYY"
            showDateOnly
            value={state.schedules[0].travelPeriod.to}
            minDate={travelDayjs.from.add(1, 'day')}
            onChange={(date) => dispatch({ type: 'setTravelPeriodTo', date: formatDateISO(date) })}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}

export default ScheduledDiscount;
